import { useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { useQuery as UseQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { buildTagComparator } from '~publish/components/FilterByTag'
import { useDebouncedTask } from '~publish/components/ContentItemCard/connectionRefresh'
import { ContentItemCard_ContentItem } from '~publish/components/ContentItemCard/fragments/ContentItemCard_ContentItem'
import { PostCard_Post } from '~publish/components/PostCard/fragments/PostCard_Post'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'
import { PostNoteTotal } from '~publish/hooks/notes/fragments/PostNoteTotal'
import { PostUnreadMentionCount } from '~publish/hooks/notes/fragments/PostUnreadMentionCount'
import { PostUnreadNoteCount } from '~publish/hooks/notes/fragments/PostUnreadNoteCount'
import { useNoteCountVariables } from '~publish/hooks/notes/useNoteCountVariables'
import type { NoteCountSelection } from '~publish/hooks/notes/useNoteCountVariables'
import { isOptionalNotesSummaryError } from '~publish/hooks/notes/isOptionalNotesSummaryError'

export const DRAFTS_PER_PAGE = 20

// Module scope keeps the array identity stable, so the listeners bind once.
const CONTENT_ITEM_EVENTS = [
  PusherEvent.CONTENT_ITEM_CREATED,
  PusherEvent.CONTENT_ITEM_UPDATED,
  PusherEvent.CONTENT_ITEM_DELETED,
]

// Each can put a post into drafts, change its date, or take it out: an edit
// that adds a custom time, Add to Queue, Move to Drafts, an approval.
const POST_EVENTS = [
  PusherEvent.POST_CREATED,
  PusherEvent.POST_UPDATED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_APPROVED,
]

// These only take a post out, so they matter only for a post the list holds.
// Every queued post sends `sent_update`, which would otherwise refetch drafts
// all day.
const POST_REMOVED_EVENTS = [PusherEvent.POST_SENT, PusherEvent.POST_DELETED]

type PusherPostEventData = {
  update_id?: string
  draft_id?: string
  profile_id?: string
  /** The stored post. `status` is the raw one: an approval draft is `draft` too. */
  update?: { status?: string }
}

export const GetDraftsList = graphql(
  `
    query GetDraftsList(
      $first: Int!
      $after: String
      $organizationId: OrganizationId!
      $sort: [DraftsSortInput!]
      $channel: DraftsChannelFilter
      $tags: TagComparator
      $includeUnreadNoteCount: Boolean! = false
      $includeNotesButtonCounts: Boolean! = false
    ) {
      drafts(
        input: {
          organizationId: $organizationId
          sort: $sort
          filter: { channel: $channel, tags: $tags }
        }
        first: $first
        after: $after
      ) {
        edges {
          cursor
          date
          # \`node\` is aliased per branch — \`Post.id\`/\`ContentItem.id\` resolve to
          # different named ID scalars, and a validator these fragments share
          # rejects two same-named-but-differently-typed \`id\` fields inside one
          # selection set, even across a union's non-overlapping members.
          # Aliasing keeps each branch's fields in its own selection set.
          postNode: node {
            __typename
            ... on Post {
              id
              dueAt
              sentAt
              ...PostCard_Post
              ...CalendarPostCard_Post
              ...PostUnreadNoteCount @include(if: $includeUnreadNoteCount)
              ...PostUnreadMentionCount @include(if: $includeNotesButtonCounts)
              ...PostNoteTotal @include(if: $includeNotesButtonCounts)
            }
          }
          contentItemNode: node {
            __typename
            ... on ContentItem {
              id
              ...ContentItemCard_ContentItem
            }
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
      }
    }
  `,
  [
    PostCard_Post,
    CalendarPostCard_Post,
    ContentItemCard_ContentItem,
    PostUnreadNoteCount,
    PostUnreadMentionCount,
    PostNoteTotal,
  ],
)

type GetDraftsListData = ResultOf<typeof GetDraftsList>
type GetDraftsListVariables = VariablesOf<typeof GetDraftsList>

export type DraftsSortInput = NonNullable<
  GetDraftsListVariables['sort']
>[number]
export type DraftEdge = GetDraftsListData['drafts']['edges'][number]

/** The edge's node id, whichever branch of the union actually matched. */
export function getDraftEdgeId(edge: DraftEdge): string | undefined {
  if (isOfType(edge.postNode, 'Post')) return edge.postNode.id
  if (isOfType(edge.contentItemNode, 'ContentItem')) {
    return edge.contentItemNode.id
  }
  return undefined
}

// Content date ascending, ties broken by newest-created-first — matches the
// dueAt/targetDate order the two separate posts and content-item queries used.
const DEFAULT_SORT: DraftsSortInput[] = [
  { field: 'date', direction: 'asc' },
  { field: 'createdAt', direction: 'desc' },
]

type UseDraftsListOptions = {
  perPage?: number
  sort?: DraftsSortInput[]
  /** Narrows posts to these channels. Omit for no channel restriction; an empty array is a deliberate "no real channels" selection, only meaningful combined with `includeChannellessDrafts`. */
  channelIds?: string[]
  /** Includes content item drafts alongside a `channelIds` match. With `channelIds` empty, this decides between no results and channel-less-only results. */
  includeChannellessDrafts?: boolean
  /** Narrows both kinds of draft to these tags — content items carry tags too, unlike channel/post-type filters. */
  tagIds?: string[]
  /** The Note counts this list renders. Defaults to none. */
  noteCounts?: NoteCountSelection
}

type UseDraftsListResult = {
  data: GetDraftsListData | undefined
  loading: boolean
  fetchingMore: boolean
  /** A filter/sort change swapped in new variables the cache has no entry for yet — `data` goes briefly undefined. */
  reloading: boolean
  error: UseQuery.Result<GetDraftsListData, GetDraftsListVariables>['error']
  lastElementRef: UseIntersectionObserverReturn[0]
  refetch: UseQuery.Result<GetDraftsListData, GetDraftsListVariables>['refetch']
}

/**
 * Paginated list of an organization's drafts: channel-specific draft posts
 * and channel-less content items in one ordered stream, via the unified
 * `drafts` query. One cursor covers the whole page, so a record between
 * "fetched" and "displayed" can't fall between two independently-advancing
 * cursors the way the earlier two-query merge allowed.
 */
export function useDraftsList({
  perPage = DRAFTS_PER_PAGE,
  sort = DEFAULT_SORT,
  channelIds,
  includeChannellessDrafts = false,
  tagIds,
  noteCounts = 'none',
}: UseDraftsListOptions = {}): UseDraftsListResult {
  const organizationId = useOrganizationId()
  const noteCountVariables = useNoteCountVariables(noteCounts)

  // Omitted (not an empty-array `channelIds`) means "no channel filter at
  // all" server-side, so only build the filter when there's actually one to
  // apply — collapsing to `channelIds: []` here would narrow an unfiltered
  // view down to channel-less drafts only.
  const channel =
    channelIds !== undefined || includeChannellessDrafts
      ? { channelIds: channelIds ?? [], includeChannellessDrafts }
      : undefined
  const tags = buildTagComparator(tagIds)

  const { data, refetch, fetchMore, error, networkStatus } = useQuery(
    GetDraftsList,
    {
      variables: {
        first: perPage,
        after: null,
        organizationId: organizationId || '',
        ...noteCountVariables,
        sort,
        channel,
        tags,
      },
      fetchPolicy: 'cache-and-network',
      // cache-first (not cache-only) so infinite-scroll `fetchMore` can reach
      // the network; cache-only still serves cached pages without re-fetching.
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      skip: !organizationId,
    },
  )

  // Data-first: a background validation can report loading/setVariables even
  // when the cache already has data for these variables — gate on both, or
  // cached content flashes a skeleton for no reason.
  const loading = networkStatus === NetworkStatus.loading && !data?.drafts
  const fetchingMore = networkStatus === NetworkStatus.fetchMore
  const reloading =
    networkStatus === NetworkStatus.setVariables && !data?.drafts
  const draftsError =
    data?.drafts && isOptionalNotesSummaryError(error) ? undefined : error
  const endCursor = data?.drafts.pageInfo.endCursor

  // `drafts` mixes posts and content items under one sort (see the `date`
  // alias above), so this list refetches instead of placing the item. The
  // tradeoff: `drafts` has no merge policy, so a refetch drops the pages a
  // user scrolled into. The debounce turns a burst of events into one
  // refetch. Content items appear here unless a channel filter leaves them
  // out.
  const scheduleRefetch = useDebouncedTask(refetch, 'drafts list refetch')
  usePusherEvent(CONTENT_ITEM_EVENTS, (): void => {
    if (channel && !channel.includeChannellessDrafts) return
    scheduleRefetch()
  })

  // A post's `date` lives on its `drafts` edge, and its membership in the
  // connection is server-side, so a normalized write to the Post cannot move
  // it or drop it. The same refetch settles both.
  const isPostChannelInView = (channelId?: string): boolean =>
    !channel || (!!channelId && channel.channelIds.includes(channelId))
  const isPostLoaded = (postId?: string): boolean =>
    !!postId &&
    !!data?.drafts.edges.some((edge) => getDraftEdgeId(edge) === postId)
  usePusherEvent(POST_EVENTS, (event: PusherPostEventData): void => {
    if (!isPostChannelInView(event.profile_id)) return
    // A post the list does not hold matters only if it is now a draft.
    // Skipping known non-drafts keeps queued-post edits on a busy org from
    // refetching this list, and from starving the debounce.
    const status = event.update?.status
    const isKnownNonDraft = status !== undefined && status !== 'draft'
    if (isKnownNonDraft && !isPostLoaded(event.update_id ?? event.draft_id)) {
      return
    }
    scheduleRefetch()
  })
  usePusherEvent(POST_REMOVED_EVENTS, (event: PusherPostEventData): void => {
    if (!isPostLoaded(event.update_id ?? event.draft_id)) return
    scheduleRefetch()
  })

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.drafts.pageInfo.hasNextPage,
    fetchMore: async () => {
      try {
        await fetchMore({
          variables: { after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult?.drafts?.edges) return prev

            const newEdges = fetchMoreResult.drafts.edges
            const newIds = new Set(newEdges.map(getDraftEdgeId))
            const existingEdges = (prev.drafts?.edges ?? []).filter(
              (edge) => !newIds.has(getDraftEdgeId(edge)),
            )

            return {
              ...prev,
              drafts: {
                ...prev.drafts,
                edges: [...existingEdges, ...newEdges],
                pageInfo: fetchMoreResult.drafts.pageInfo,
              },
            }
          },
        })
      } catch (caughtError) {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Unknown error'

        toast.error('Error fetching more drafts')
        throw new Error(`Failed to fetch more drafts: ${message}`, {
          cause: caughtError,
        })
      }
    },
  })

  return useMemo(
    () => ({
      data,
      loading,
      fetchingMore,
      reloading,
      error: draftsError,
      lastElementRef,
      refetch,
    }),
    [
      data,
      loading,
      fetchingMore,
      reloading,
      draftsError,
      lastElementRef,
      refetch,
    ],
  )
}
